import { useReactiveVar } from '@apollo/client';
import { useCallback, useRef, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';

import { SubmitErrorAlert } from '@/components/Alerts';
import Card from '@/components/Card';
import Modal from '@/components/Modal';
import { useUpdateAccountMutation } from '@/graphql';
import { AccountForm } from '@/routes/Agency/common/components';
import { AccountFormValues } from '@/routes/Agency/common/components/AccountForm';
import { isTenantAdminUser } from '@/util/adminRole';
import { Maybe } from '@/types';
import { GetAccountQuery, AccountRateTypeEnum } from '@/types/graphql';
import { currentAdminVar } from '@/util/apollo/cache';
import { trackClientProfileUpdated } from '@/util/vexo';

export type Props = {
  account: GetAccountQuery['account'];
  hideModal: () => void;
  rateType?: string;
};

const UpdateContactModal = ({ account, hideModal, rateType }: Props) => {
  const [submitError, setSubmitError] = useState<Maybe<Error>>(null);
  // Captured during onSubmit, read inside onCompleted so the Vexo event
  // ships only the fields that actually changed (no over-reporting when
  // a user edits one field but the modal saves the whole form).
  const pendingFieldsUpdatedRef = useRef<string[]>([]);

  const [updateAccountInformation, { loading }] = useUpdateAccountMutation({
    update: (cache) => {
      cache.modify({
        id: cache.identify(account),
        fields: {
          defaultContact() {},
          addressLine1() {},
          addressLine2() {},
          city() {},
          state() {},
          zip() {},
        },
      });
    },
    onCompleted: () => {
      // Vexo `client_profile_updated` (CSV #3). The modal updates contact
      // + address as a single form, and tenant-admins can also adjust
      // markup. We send section-level field NAMES (not values) per the
      // CSV PII rule. `update_type: 'manual'` — this only fires from the
      // user-initiated edit modal; the 'prompted' path (banner/onboarding
      // nudge) doesn't exist here. Skip the fire when nothing actually
      // changed (e.g. user opens the modal and clicks save unchanged).
      const fieldsUpdated = pendingFieldsUpdatedRef.current;
      if (fieldsUpdated.length > 0) {
        trackClientProfileUpdated({
          fields_updated: fieldsUpdated,
          update_type: 'manual',
        });
      }
      hideModal();
    },
    onError: setSubmitError,
  });

  const currentAdmin = useReactiveVar(currentAdminVar);
  const isTenantAdmin = isTenantAdminUser(currentAdmin);

  const onSubmit: SubmitHandler<AccountFormValues> = useCallback(
    ({ defaultContact, address, markupPercent = '' }) => {
      const markupPercentageValue = isTenantAdmin
        ? Number(markupPercent?.replace('%', ''))
        : account?.markupPercent;
      // Diff submitted values against the current `account` so the
      // `fields_updated` array only carries what actually changed.
      const fieldsUpdated: string[] = [];
      if (defaultContact.id !== account.defaultContact.id) {
        fieldsUpdated.push('contact');
      }
      const addressChanged =
        address.addressLine1 !== (account.addressLine1 ?? '') ||
        (address.addressLine2 ?? null) !== (account.addressLine2 ?? null) ||
        address.city !== (account.city ?? '') ||
        address.state !== (account.state ?? '') ||
        address.zip !== (account.zip ?? '');
      if (addressChanged) fieldsUpdated.push('address');
      if (
        isTenantAdmin &&
        rateType === AccountRateTypeEnum.MARKUP &&
        Number(markupPercentageValue) !== Number(account?.markupPercent)
      ) {
        fieldsUpdated.push('markup_percent');
      }
      pendingFieldsUpdatedRef.current = fieldsUpdated;
      updateAccountInformation({
        // @ts-ignore
        variables: {
          accountId: account.id,
          defaultContactId: defaultContact.id,
          ...address,
          addressLine2: address.addressLine2 ?? null,
          ...(rateType === AccountRateTypeEnum.MARKUP && {
            markupPercent: markupPercentageValue,
          }),
        },
      });
    },
    [],
  );

  return (
    <Modal
      disableClickout
      size="sm"
      title={'Update Account Information'}
      wrapperBackground={true}
      onRequestClose={hideModal}
    >
      <Card.Section>
        {submitError && <SubmitErrorAlert description={submitError.message} />}
        <AccountForm
          customer={account.customer}
          defaultEmail={account.defaultContact.user.email}
          isLoading={loading}
          newAccount={false}
          rateType={rateType}
          selectedAccount={account}
          onSubmit={onSubmit}
        />
      </Card.Section>
    </Modal>
  );
};

export default UpdateContactModal;
